/**
 * The app's own translation lookup: a dot-path `t()` over bundled JSON
 * dictionaries, with English filling any key the active language lacks.
 *
 * Hand-rolled rather than i18next because the app has two small bundles and
 * one string-replacement need; the language is a module-level variable set
 * from the gardener's preferences (UserConfigProvider) so `t()` can be called
 * outside React.
 *
 * Setting it also sets the page's `<html lang>`, so a screen reader reads the
 * Spanish app in Spanish, and tells whoever listens (onLanguageChange): the
 * dates, whose words follow the app's language (dateUtils), and every screen
 * that reads it through useTranslation (subscribeToLanguage, B72: the account's
 * language arrives after the first draw, and a page reached cold must redraw in it).
 *
 * The dictionaries: locales/en/common.json (the reference; every key must be
 * there) and locales/es/common.json (held to it by scripts/i18n-check.mjs,
 * `npm run i18n`). Keys are dot paths into nested groups named by screen or
 * area (plants.card..., aiAllowance.barTitle); a value is a string, or a
 * list of strings joined with ", ". Placeholders are {name}, filled from
 * t()'s params by plain replacement: no plurals, no escaping. A plural is
 * two keys, chosen by the caller
 * (plants.card.notesCountOne / plants.card.notesCount). src/shared/README.md has the whole
 * guide, with how to add a key.
 *
 * Components use shared/hooks/useTranslation (reactTranslate); code outside
 * React imports t from here.
 *
 * @module i18nSetup
 */
import { Language } from '../types/i18n';
import { CommonTranslations } from './types/common';
import commonJson from './locales/en/common.json';
import esCommonJson from './locales/es/common.json';

/*
 * The timeline bundles went with the timeline (see dep/). They were still
 * being merged in and shipped in the bundle, several hundred keys for screens
 * that no longer exist.
 *
 * Spanish is not a complete mirror of English, so gaps are filled at lookup
 * time by falling back to English rather than by shipping a half-translated
 * file that renders raw key names on screen.
 */
/** The English dictionary: the reference, and every other language's fallback. */
const en = commonJson as CommonTranslations;
/** The Spanish dictionary; a key it lacks is said in English. */
const es = esCommonJson as unknown as CombinedTranslations;

/** One dictionary entry: a string, a list joined for display, or a nested group. */
type TranslationValue = string | string[] | { [key: string]: TranslationValue };
/** A whole language's dictionary, typed by the English shape but open to extra keys. */
type CombinedTranslations = CommonTranslations & {
  [key: string]: TranslationValue;
};

/** The languages the app ships, by code, each named in its own language for a picker. */
export const languages: Partial<Record<Language, string>> = {
  en: 'English',
  es: 'Español',
};

/** Every shipped dictionary, by language code. */
const translations: Partial<Record<Language, CombinedTranslations>> = {
  en,
  es,
};

/** Falls back to English for any key the active language is missing. */
const FALLBACK_LANGUAGE: Language = 'en';

/** The language `t()` looks in first; set from the gardener's preferences. */
let currentLanguage: Language = 'en';

/** Who is told when the language changes (onLanguageChange). */
const languageListeners = new Set<(lang: Language) => void>();

/**
 * Sets the current language, the page's `<html lang>` with it, and tells the
 * listeners. Can be called at any time, before React or after.
 *
 * @param lang - The language to speak; one not shipped falls back to English.
 */
export const setLanguage = (lang: Language): void => {
  if (translations[lang]) {
    currentLanguage = lang;
  } else {
    console.warn(`Language ${lang} not supported. Using fallback language: ${FALLBACK_LANGUAGE}`);
    currentLanguage = FALLBACK_LANGUAGE;
  }
  try {
    if (typeof document !== 'undefined') document.documentElement.lang = currentLanguage;
  } catch { /* no page to mark */ }
  languageListeners.forEach((listener) => listener(currentLanguage));
};

/**
 * Hears every change of language, and the current one at once, so it does
 * not matter whether the listener or the first setLanguage comes first.
 *
 * @param listener - Called with the language now and on each change.
 * @returns A function that stops it hearing.
 */
export const onLanguageChange = (listener: (lang: Language) => void): (() => void) => {
  languageListeners.add(listener);
  listener(currentLanguage);
  return () => { languageListeners.delete(listener); };
};

/**
 * Hears every later change of language (not the current one): what React's useSyncExternalStore subscribes with, so
 * a component that reads the language redraws when it changes (useTranslation). It is told a moment after the change
 * (a microtask), not inside it: a page that changes the language while it draws (the gift, stop-subscription and
 * Monday-email pages do, from their address's ?lang) must not ask other components to redraw in the middle of its own draw.
 *
 * @param listener - Called after each change of language.
 * @returns A function that stops it hearing.
 */
export const subscribeToLanguage = (listener: () => void): (() => void) => {
  /** The listener, called a moment later (a microtask) instead of inside the change. */
  const wrapped = (): void => queueMicrotask(listener);
  languageListeners.add(wrapped);
  return () => { languageListeners.delete(wrapped); };
};

/**
 * The language `t()` speaks now.
 *
 * @returns Its code.
 */
export const getLanguage = (): Language => currentLanguage;

/**
 * Translates a key with optional parameters.
 *
 * Looks in the current language, then English, then params.defaultValue;
 * with none of those it warns and returns the key itself (which is what
 * scripts/i18n-check.mjs exists to prevent).
 *
 * @param key The translation key (dot notation for nested properties)
 * @param params Optional parameters to replace in the translation string:
 *   each {name} in it becomes params.name. A `defaultValue` among them is
 *   what to say when no language has the key (instead of the key itself),
 *   as in i18next.
 * @returns The words, parameters filled in; a list joined with ", "; a
 *   group (a key that stops short of a string) as String() of it.
 */
export const t = (key: string, params?: Record<string, string | number>): string => {
  const keyPath = key.split('.');

  /**
   * Walk a dot path into one language's dictionary.
   *
   * @param lang - Language to look in.
   * @returns The value, or undefined when that language does not carry the key.
   */
  const lookup = (lang: Language): TranslationValue | undefined => {
    let node: TranslationValue | undefined = translations[lang] as CombinedTranslations;
    for (const k of keyPath) {
      node = (node as { [key: string]: TranslationValue } | undefined)?.[k];
      if (node === undefined) return undefined;
    }
    return node;
  };

  // English fills the gaps. The Spanish bundle is not a complete mirror of the
  // English one, and returning the key itself put raw key names on screen.
  let translation = lookup(currentLanguage);
  if (translation === undefined && currentLanguage !== FALLBACK_LANGUAGE) {
    translation = lookup(FALLBACK_LANGUAGE);
  }
  if (translation === undefined && params?.defaultValue !== undefined) {
    translation = String(params.defaultValue);
  }
  if (translation === undefined) {
    console.warn(`Translation key not found: ${key}`);
    return key;
  }
  // If the translation is not a string, return it as is
  if (Array.isArray(translation)) {
    return translation.join(', ');
  }
  if (typeof translation !== 'string') {
    return String(translation);
  }
  
  // Replace parameters in the translation string
  if (params) {
    return Object.entries(params).reduce((str, [paramKey, paramValue]) => {
      return str.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
    }, translation);
  }
  
  return translation;
};